/**
 * ScheduleMessageDialog v2.0.0
 * -----------------------------------------------------------------------------
 * Diálogo premium para agendar mensagens no chat. Reaproveita o layout
 * split-pane do ReminderRuleFormDialog (formulário à esquerda, preview do
 * balão em tempo real à direita), adaptado ao contexto de agendamento:
 *
 *   - Canal derivado da conversa (somente leitura, exibido como badge)
 *   - Data/hora de envio (datetime-local, mínimo agora + 1 min)
 *   - Textarea com formatação WhatsApp (*negrito*, _itálico_, ~tachado~),
 *     emoji picker e chips de variáveis ({{contact_name}}, etc.)
 *   - Switch "cancelar se contato responder" + nota interna
 *
 * Mantém a integração intacta com as RPCs `schedule_chat_message` e
 * `update_scheduled_chat_message` — nenhuma mudança no backend.
 *
 * RELEASE:
 *   2026-06-02 v1.0.0 - MVP compacto (substituído nesta versão).
 *   2026-06-02 v2.0.0 - Redesign premium split-pane com preview ao vivo,
 *                       formatação WhatsApp, emoji picker e variáveis de chat.
 *   2026-06-02 v2.1.0 - Vinculação opcional de Ação Rápida (macro) ao
 *                       agendamento, com seleção de modo (antes/depois/substituir).
 *   2026-06-12 v2.2.0 - Suporte a agendamento de TEMPLATE para WhatsApp Oficial:
 *                       modos `text`, `template` e `text_with_fallback` (envia
 *                       texto se janela 24h aberta; senão dispara template).
 *                       Banner de status da janela 24h. Demais canais permanecem
 *                       inalterados (apenas texto).
 */


import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CalendarClock,
  Loader2,
  Sparkles,
  Settings,
  MessageSquare,
  Variable,
  Eye,
  Bold,
  Italic,
  Strikethrough,
  StickyNote,
  Zap,
  X,
  FileText,
  AlertTriangle,
  CheckCircle2,
  History,
} from 'lucide-react';
import { ConversationScheduledMessagesModal } from './scheduled/ConversationScheduledMessagesModal';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';
import { ReminderMessagePreview } from '@/components/reminders/preview/ReminderMessagePreview';
import { EmojiPicker } from './EmojiPicker';
import { useSessionWindow } from '@/hooks/useSessionWindow';
import {
  ScheduledTemplatePicker,
  type ScheduledTemplateValue,
} from './scheduled/ScheduledTemplatePicker';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { InlineNotice } from '@/components/ui/inline-notice';


// =============================================================================
// TIPOS
// =============================================================================

export interface ScheduleMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Conversa em que a mensagem será agendada (obrigatório em criação). */
  conversationId?: string;
  /** Em modo edição, ID da entrada de reminder_queue. */
  scheduledId?: string;
  /** Valores iniciais para edição. */
  initial?: {
    scheduledAt?: string;
    text?: string;
    cancelOnReply?: boolean;
    internalNote?: string;
    /** v2.1.0 — macro vinculada (modo edição) */
    linkedMacroRuleId?: string | null;
    linkedMacroMode?: 'after_message' | 'before_message' | 'replace_message' | null;
    /** v2.3.0 — payload bruto (reminder_queue.message_payload) para hidratar
     *  modo da mensagem (text / template / text_with_fallback) e os pickers
     *  de template/fallback ao abrir em edição ou visualização. */
    messagePayload?: any;
  };

  /** Contexto da inbox (para exibir badge do canal e renderizar o preview). */
  conversationContext?: {
    inboxId?: string;
    inboxName?: string;
    channelType?: string;
    /** v2.2.0 — Timestamp da última msg inbound (calcula janela 24h). */
    lastInboundAt?: string | null;
  };
  /** Callback após sucesso (criar ou editar). */
  onSuccess?: (id: string) => void;
  /** v1.2.0 — Quando true, abre apenas para visualização: todos os campos e ações desabilitados. */
  readOnly?: boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

/** Converte Date → string aceita por `<input type="datetime-local">`. */
function toLocalDateTimeInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** Variáveis disponíveis no agendamento de chat (alinhadas ao worker). */
const CHAT_VARIABLES: { key: string; label: string }[] = [
  { key: '{{contact_name}}', label: 'Nome do contato' },
  { key: '{{first_name}}', label: 'Primeiro nome' },
  { key: '{{contact_phone}}', label: 'Telefone' },
  { key: '{{agent_name}}', label: 'Nome do agente' },
];

/** Mapeamento amigável do tipo de canal para preview. */
function getChannelLabel(channelType?: string): string {
  if (!channelType) return 'Canal';
  const map: Record<string, string> = {
    whatsapp: '📱 WhatsApp',
    whatsapp_oficial: '📱 WhatsApp Oficial',
    sms: '💬 SMS',
    email: '📧 Email',
    telegram: '✈️ Telegram',
    instagram: '📸 Instagram',
    webchat: '🌐 Webchat',
  };
  return map[channelType.toLowerCase()] || channelType;
}

/**
 * Envolve a seleção atual do textarea com um marcador de formatação WhatsApp
 * (`*` negrito, `_` itálico, `~` tachado). Se não houver seleção, insere o par
 * vazio e posiciona o cursor entre eles.
 */
function applyFormatting(
  textarea: HTMLTextAreaElement | null,
  marker: string,
  setValue: (val: string) => void,
) {
  if (!textarea) return;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const current = textarea.value;
  const selected = current.substring(start, end);
  const wrapped = selected ? `${marker}${selected}${marker}` : `${marker}${marker}`;
  const next = current.substring(0, start) + wrapped + current.substring(end);
  setValue(next);
  requestAnimationFrame(() => {
    textarea.focus();
    const cursor = selected ? start + wrapped.length : start + marker.length;
    textarea.setSelectionRange(cursor, cursor);
  });
}

// =============================================================================
// COMPONENTE
// =============================================================================

export function ScheduleMessageDialog({
  open,
  onOpenChange,
  conversationId,
  scheduledId,
  initial,
  conversationContext,
  onSuccess,
  readOnly = false,
}: ScheduleMessageDialogProps) {
  const isEdit = Boolean(scheduledId);
  // Controle do modal de Histórico (listagem reaproveitada)
  const [openHistory, setOpenHistory] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Default: agora + 15 minutos, arredondado para o minuto cheio
  const defaultDate = useMemo(() => {
    const d = new Date(Date.now() + 15 * 60 * 1000);
    d.setSeconds(0, 0);
    return toLocalDateTimeInput(d);
  }, []);

  const [scheduledAt, setScheduledAt] = useState<string>(defaultDate);
  const [text, setText] = useState<string>('');
  const [cancelOnReply, setCancelOnReply] = useState<boolean>(true);
  const [internalNote, setInternalNote] = useState<string>('');
  const [saving, setSaving] = useState(false);
  // v2.1.0: macro vinculada (opcional)
  const [linkedMacroRuleId, setLinkedMacroRuleId] = useState<string>('');
  const [linkedMacroMode, setLinkedMacroMode] = useState<
    'after_message' | 'before_message' | 'replace_message'
  >('after_message');

  // v2.2.0: modo da mensagem (somente exibido para WhatsApp Oficial).
  type MessageMode = 'text' | 'template' | 'text_with_fallback';
  const [messageMode, setMessageMode] = useState<MessageMode>('text');
  const [templateValue, setTemplateValue] = useState<ScheduledTemplateValue | null>(null);
  const [fallbackTemplate, setFallbackTemplate] = useState<ScheduledTemplateValue | null>(null);

  const inboxId = conversationContext?.inboxId;
  const channelType = conversationContext?.channelType;
  const lastInboundAt = conversationContext?.lastInboundAt;

  // v2.2.0: janela 24h (somente WhatsApp Oficial). Para outros canais o hook
  // retorna appliesSessionWindow=false, então a UI extra é ocultada.
  const sessionWindow = useSessionWindow(channelType, lastInboundAt ?? null);
  const showWabaMode = sessionWindow.isWhatsAppOficial;

  // Ref usada para hidratar UMA única vez por abertura do diálogo.
  // Sem isso, o `initial` (objeto novo a cada render do pai) faria o effect
  // re-executar e resetaria o formulário — inclusive a data escolhida no
  // datetime-local, que "piscava" e voltava ao valor original.
  const hydratedKeyRef = useRef<string | null>(null);
  // Ref sempre atualizada com o `initial` mais recente para leitura dentro do effect
  const initialRef = useRef(initial);
  initialRef.current = initial;

  // Hidrata o formulário ao abrir (uma vez por agendamento)
  useEffect(() => {
    if (!open) {
      // Ao fechar, libera para próxima hidratação
      hydratedKeyRef.current = null;
      return;
    }
    const key = scheduledId ?? 'new';
    if (hydratedKeyRef.current === key) return;
    hydratedKeyRef.current = key;

    const init = initialRef.current;
    if (init) {
      if (init.scheduledAt) {
        setScheduledAt(toLocalDateTimeInput(new Date(init.scheduledAt)));
      } else {
        setScheduledAt(defaultDate);
      }
      setText(init.text ?? '');
      setCancelOnReply(init.cancelOnReply ?? true);
      setInternalNote(init.internalNote ?? '');
      setLinkedMacroRuleId(init.linkedMacroRuleId ?? '');
      setLinkedMacroMode(init.linkedMacroMode ?? 'after_message');

      // v2.3.0 — Hidrata modo da mensagem + pickers a partir do payload
      // bruto (reminder_queue.message_payload). Garante que ao reabrir uma
      // mensagem agendada como template ou fallback, a UI reflita o que foi
      // realmente salvo.
      const payload = init.messagePayload;
      const tpl = payload?.template;
      const fb = payload?.fallback_template;
      if (tpl?.name) {
        setMessageMode('template');
        setTemplateValue({
          templateName: tpl.name,
          templateLanguage: tpl.language ?? 'pt_BR',
          bodyParams: Array.isArray(tpl.body_params) ? tpl.body_params : [],
          bodyTextRendered: '',
        });
        setFallbackTemplate(null);
      } else if (fb?.name) {
        setMessageMode('text_with_fallback');
        setFallbackTemplate({
          templateName: fb.name,
          templateLanguage: fb.language ?? 'pt_BR',
          bodyParams: Array.isArray(fb.body_params) ? fb.body_params : [],
          bodyTextRendered: '',
        });
        setTemplateValue(null);
      } else {
        setMessageMode('text');
        setTemplateValue(null);
        setFallbackTemplate(null);
      }
    } else {
      setScheduledAt(defaultDate);
      setText('');
      setCancelOnReply(true);
      setInternalNote('');
      setLinkedMacroRuleId('');
      setLinkedMacroMode('after_message');
      setMessageMode('text');
      setTemplateValue(null);
      setFallbackTemplate(null);
    }
  }, [open, scheduledId, defaultDate]);



  // v2.1.0: Lista de macros (ações rápidas) disponíveis para esta inbox.
  // Mesma RPC usada pelo QuickActionsPopover — respeita visibility/ownership.
  const { data: availableMacros = [], isLoading: macrosLoading } = useQuery({
    queryKey: ['schedule-msg-macros', inboxId],
    queryFn: async () => {
      if (!inboxId) return [] as Array<{ id: string; name: string; description: string | null }>;
      const { data, error } = await supabase.rpc('get_manual_routing_rules', {
        p_inbox_id: inboxId,
      });
      if (error) {
        logger.error('ScheduleMessageDialog', 'Erro ao buscar macros', error);
        return [];
      }
      return (data || []) as Array<{ id: string; name: string; description: string | null }>;
    },
    enabled: open && !!inboxId,
    staleTime: 30000,
  });


  /** Insere variável `{{...}}` na posição do cursor do textarea. */
  const insertVariable = useCallback((token: string) => {
    const ta = textareaRef.current;
    if (!ta) {
      setText((prev) => prev + token);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    setText((prev) => prev.substring(0, start) + token + prev.substring(end));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  }, []);

  /** Insere emoji na posição do cursor. */
  const insertEmoji = useCallback((emoji: string) => {
    const ta = textareaRef.current;
    if (!ta) {
      setText((prev) => prev + emoji);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    setText((prev) => prev.substring(0, start) + emoji + prev.substring(end));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + emoji.length;
      ta.setSelectionRange(pos, pos);
    });
  }, []);

  /**
   * Constrói o payload conforme o modo selecionado.
   * v2.2.0: suporta `text`, `template` e `text_with_fallback`.
   */
  const buildPayload = (): { ok: true; payload: any } | { ok: false; err: string } => {
    const trimmed = text.trim();

    // Modo TEMPLATE (somente WABA): obriga template selecionada com todas
    // as variáveis preenchidas.
    if (showWabaMode && messageMode === 'template') {
      if (!templateValue) return { ok: false, err: 'Selecione uma template aprovada' };
      if (templateValue.bodyParams.some((p) => !p.trim())) {
        return { ok: false, err: 'Preencha todas as variáveis da template' };
      }
      return {
        ok: true,
        payload: {
          kind: 'template',
          template: {
            name: templateValue.templateName,
            language: templateValue.templateLanguage,
            body_params: templateValue.bodyParams,
          },
        },
      };
    }

    // Modos `text` e `text_with_fallback` exigem texto.
    if (!trimmed) return { ok: false, err: 'Digite o texto da mensagem' };

    const payload: any = { kind: 'text', text: trimmed };

    // Fallback: anexa metadados de template para o worker disparar caso
    // a janela 24h esteja fechada no momento do envio.
    if (showWabaMode && messageMode === 'text_with_fallback') {
      if (!fallbackTemplate) {
        return { ok: false, err: 'Selecione a template de fallback' };
      }
      if (fallbackTemplate.bodyParams.some((p) => !p.trim())) {
        return { ok: false, err: 'Preencha todas as variáveis do fallback' };
      }
      payload.fallback_template = {
        name: fallbackTemplate.templateName,
        language: fallbackTemplate.templateLanguage,
        body_params: fallbackTemplate.bodyParams,
      };
    }

    return { ok: true, payload };
  };

  /** Valida campos e chama a RPC apropriada. */
  const handleSave = async () => {
    const when = new Date(scheduledAt);
    if (Number.isNaN(when.getTime())) {
      toast.error('Data/hora inválida');
      return;
    }
    if (when.getTime() < Date.now() + 60 * 1000) {
      toast.error('Agende para pelo menos 1 minuto no futuro');
      return;
    }

    const built = buildPayload();
    if (built.ok === false) {
      toast.error(built.err);
      return;
    }

    setSaving(true);
    try {
      const payload = built.payload;
      const macroId = linkedMacroRuleId || null;

      if (isEdit && scheduledId) {
        const { error } = await supabase.rpc('update_scheduled_chat_message', {
          p_id: scheduledId,
          p_scheduled_at: when.toISOString(),
          p_payload: payload,
          p_cancel_on_reply: cancelOnReply,
          p_internal_note: internalNote || undefined,
          // v2.1.0 — macro vinculada (set, troca ou limpa)
          p_linked_macro_rule_id: macroId || undefined,
          p_linked_macro_mode: macroId ? linkedMacroMode : undefined,
          p_clear_linked_macro: !macroId && !!initial?.linkedMacroRuleId,
        } as never);
        if (error) throw error;
        toast.success('Agendamento atualizado');
        onSuccess?.(scheduledId);
      } else {
        if (!conversationId) {
          toast.error('Conversa não identificada');
          return;
        }
        const { data, error } = await supabase.rpc('schedule_chat_message', {
          p_conversation_id: conversationId,
          p_scheduled_at: when.toISOString(),
          p_payload: payload,
          p_cancel_on_reply: cancelOnReply,
          p_internal_note: internalNote || undefined,
          // v2.1.0
          p_linked_macro_rule_id: macroId || undefined,
          p_linked_macro_mode: macroId ? linkedMacroMode : undefined,
        } as never);
        if (error) throw error;
        toast.success('Mensagem agendada');
        onSuccess?.(data as string);
      }

      onOpenChange(false);
    } catch (err: any) {
      logger.error('[ScheduleMessageDialog] Erro ao salvar:', err);
      toast.error(err?.message || 'Erro ao salvar agendamento');
    } finally {
      setSaving(false);
    }
  };

  const inboxName = conversationContext?.inboxName;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-4xl p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col"
        hideCloseButton={false}
      >
        {/* ===== HEADER PREMIUM ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
          <div className="flex items-start justify-between gap-3">
            <DialogHeader className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 text-base">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                </div>
                {readOnly
                  ? 'Visualizar agendamento'
                  : isEdit
                    ? 'Editar agendamento'
                    : 'Agendar mensagem'}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                {readOnly
                  ? 'Mensagem em modo somente leitura — não pode ser editada.'
                  : 'Programe o envio automático no horário escolhido. A mensagem segue pelo mesmo canal da conversa atual.'}
              </DialogDescription>
            </DialogHeader>
            {/* Botão Histórico — só em criação com conversa válida */}
            {!isEdit && !readOnly && conversationId && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setOpenHistory(true)}
                className="shrink-0 h-8 gap-2 sm:mr-8"
                aria-label="Ver histórico de agendamentos"
              >
                <History className="h-4 w-4" />
                <span className="hidden sm:inline">Histórico</span>
              </Button>
            )}
          </div>
        </div>

        {/* ===== CORPO SPLIT-PANE ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,340px] min-h-0 flex-1">
          {/* ===== PAINEL ESQUERDO — FORMULÁRIO ===== */}
          <ScrollArea className="max-h-[calc(90vh-140px)]">
            <div className="p-5 space-y-4">
              {/* --- SEÇÃO: Canal & Disparo --- */}
              <FormSectionCard title="Canal & Disparo" icon={Settings}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Canal — somente leitura, derivado da conversa */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Canal de envio</Label>
                    <div className="flex items-center gap-2 h-9 px-3 rounded-md border bg-muted/40">
                      <Badge variant="secondary" className="text-[10px] h-5">
                        {getChannelLabel(channelType)}
                      </Badge>
                      {inboxName && (
                        <span className="text-xs text-muted-foreground truncate">
                          {inboxName}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Data/hora */}
                  <div className="space-y-1.5">
                    <Label htmlFor="sched-when" className="text-xs">
                      Enviar em
                    </Label>
                    <Input
                      id="sched-when"
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      min={toLocalDateTimeInput(new Date(Date.now() + 60 * 1000))}
                      className="h-9 text-xs"
                      disabled={readOnly}
                    />
                  </div>
                </div>
              </FormSectionCard>

              {/* ----------------------------------------------------------- */}
              {/* v2.2.0 — Banner de janela 24h + seletor de modo (somente   */}
              {/* WhatsApp Oficial). Para demais canais não exibe nada e o   */}
              {/* fluxo permanece idêntico (apenas texto).                   */}
              {/* ----------------------------------------------------------- */}
              {showWabaMode && (
                <FormSectionCard title="WhatsApp Oficial — Janela 24h" icon={FileText}>
                  {/* Banner de status */}
                  {/* Padrão InlineNotice: sem fundo, barra lateral + ícone colorido */}
                  <InlineNotice
                    variant={sessionWindow.isExpired ? 'warning' : 'success'}
                    className="text-xs"
                  >
                    {sessionWindow.isExpired ? (
                      <>
                        <strong>Janela 24h fechada.</strong>{' '}
                        Mensagens de texto livres falharão neste momento. Use uma
                        template aprovada ou configure o fallback abaixo.
                      </>
                    ) : (
                      <>
                        <strong>Janela 24h aberta</strong> — restam{' '}
                        <span className="font-mono">{sessionWindow.remainingTime}</span>.
                        {' '}Pode enviar texto livre, mas considere um fallback caso o
                        contato não responda até o horário do disparo.
                      </>
                    )}
                  </InlineNotice>

                  {/* Seletor de modo */}
                  <div className="pt-3">
                    <Label className="text-xs mb-2 block">Modo da mensagem</Label>
                    <RadioGroup
                      value={messageMode}
                      onValueChange={(v) => setMessageMode(v as MessageMode)}
                      disabled={readOnly}
                      className="grid grid-cols-1 gap-1.5"
                    >
                      <label
                        htmlFor="mode-text"
                        className={cn(
                          'flex items-start gap-2 rounded-md border p-2 cursor-pointer transition-colors',
                          messageMode === 'text' && 'border-primary bg-primary/5',
                        )}
                      >
                        <RadioGroupItem id="mode-text" value="text" className="mt-0.5" />
                        <div className="space-y-0.5">
                          <p className="text-xs font-medium">Texto livre</p>
                          <p className="text-[10px] text-muted-foreground">
                            Envia o texto digitado. Falha se a janela 24h estiver
                            fechada no momento do disparo.
                          </p>
                        </div>
                      </label>

                      <label
                        htmlFor="mode-template"
                        className={cn(
                          'flex items-start gap-2 rounded-md border p-2 cursor-pointer transition-colors',
                          messageMode === 'template' && 'border-primary bg-primary/5',
                        )}
                      >
                        <RadioGroupItem id="mode-template" value="template" className="mt-0.5" />
                        <div className="space-y-0.5">
                          <p className="text-xs font-medium">Template aprovada</p>
                          <p className="text-[10px] text-muted-foreground">
                            Sempre dispara via template oficial (funciona com a janela
                            aberta ou fechada).
                          </p>
                        </div>
                      </label>

                      <label
                        htmlFor="mode-fallback"
                        className={cn(
                          'flex items-start gap-2 rounded-md border p-2 cursor-pointer transition-colors',
                          messageMode === 'text_with_fallback' && 'border-primary bg-primary/5',
                        )}
                      >
                        <RadioGroupItem
                          id="mode-fallback"
                          value="text_with_fallback"
                          className="mt-0.5"
                        />
                        <div className="space-y-0.5">
                          <p className="text-xs font-medium">
                            Texto + fallback para template
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            Se a janela 24h estiver aberta no disparo, envia o texto.
                            Se estiver fechada, dispara automaticamente a template
                            configurada abaixo.
                          </p>
                        </div>
                      </label>
                    </RadioGroup>
                  </div>

                  {/* Picker da template principal (modo template) */}
                  {messageMode === 'template' && (
                    <div className="pt-3 border-t border-border/40">
                      <ScheduledTemplatePicker
                        inboxId={inboxId}
                        value={templateValue}
                        onChange={setTemplateValue}
                        disabled={readOnly}
                        selectLabel="Template aprovada"
                      />
                    </div>
                  )}

                  {/* Picker da template de fallback */}
                  {messageMode === 'text_with_fallback' && (
                    <div className="pt-3 border-t border-border/40">
                      <ScheduledTemplatePicker
                        inboxId={inboxId}
                        value={fallbackTemplate}
                        onChange={setFallbackTemplate}
                        disabled={readOnly}
                        selectLabel="Template de fallback (usada se janela fechada)"
                      />
                    </div>
                  )}
                </FormSectionCard>
              )}

              {/* --- SEÇÃO: Mensagem --- */}
              {/* No modo `template` puro a textarea é ocultada — o conteúdo */}
              {/* será 100% da template selecionada acima.                   */}
              {!(showWabaMode && messageMode === 'template') && (
              <FormSectionCard title="Mensagem" icon={MessageSquare}>
                <div className="space-y-2">
                  {/* Barra de ferramentas: formatação + emoji */}
                  <div className="flex items-center gap-0.5 border rounded-md bg-background px-1 py-0.5 w-fit">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="Negrito (*texto*)"
                      onClick={() => applyFormatting(textareaRef.current, '*', setText)}
                      disabled={readOnly}
                    >
                      <Bold className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="Itálico (_texto_)"
                      onClick={() => applyFormatting(textareaRef.current, '_', setText)}
                      disabled={readOnly}
                    >
                      <Italic className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="Tachado (~texto~)"
                      onClick={() => applyFormatting(textareaRef.current, '~', setText)}
                      disabled={readOnly}
                    >
                      <Strikethrough className="h-3.5 w-3.5" />
                    </Button>
                    <div className="w-px h-5 bg-border mx-1" />
                    <EmojiPicker onSelect={insertEmoji} className="h-7 w-7" disabled={readOnly} />
                  </div>

                  {/* Textarea principal */}
                  <div className="relative">
                    <Textarea
                      ref={textareaRef}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Digite a mensagem... Use a barra acima para formatar, inserir emoji ou clique nas variáveis abaixo."
                      rows={6}
                      maxLength={4096}
                      className="resize-none text-sm"
                      disabled={readOnly}
                    />
                    <span className="absolute bottom-2 right-3 text-[10px] text-muted-foreground pointer-events-none">
                      {text.length}/4096
                    </span>
                  </div>
                </div>
              </FormSectionCard>
              )}

              {/* --- SEÇÃO: Variáveis disponíveis --- */}
              {!(showWabaMode && messageMode === 'template') && (
              <FormSectionCard title="Variáveis disponíveis" icon={Variable} compact>
                <p className="text-[10px] text-muted-foreground -mt-1 mb-2">
                  Clique em uma variável para inseri-la na posição do cursor.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {CHAT_VARIABLES.map((v) => (
                    <Badge
                      key={v.key}
                      variant="secondary"
                      className={cn(
                        'text-[10px] gap-1 transition-all duration-200',
                        readOnly
                          ? 'opacity-60 cursor-not-allowed'
                          : 'cursor-pointer hover:bg-primary/15 hover:text-primary hover:shadow-sm hover:scale-[1.03] active:scale-95',
                      )}
                      onClick={() => {
                        if (readOnly) return;
                        insertVariable(v.key);
                      }}
                    >
                      <span className="font-mono">{v.key}</span>
                      <span className="text-muted-foreground font-normal">
                        — {v.label}
                      </span>
                    </Badge>
                  ))}
                </div>
              </FormSectionCard>
              )}

              {/* --- SEÇÃO: Ação Rápida vinculada (v2.1.0) --- */}
              <FormSectionCard title="Ação rápida vinculada (opcional)" icon={Zap}>
                <p className="text-[11px] text-muted-foreground -mt-1">
                  Dispare uma macro (mídias, áudios, tags, fases) junto com este
                  agendamento — sem precisar reconstruir o conteúdo aqui. O
                  conteúdo executado será o da macro <span className="font-medium">no momento do disparo</span>.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-[1fr,180px] gap-2 pt-1">
                  <div className="space-y-1">
                    <Label className="text-xs">Macro</Label>
                    <div className="flex items-center gap-1.5">
                      <Select
                        value={linkedMacroRuleId || '__none__'}
                        onValueChange={(v) =>
                          setLinkedMacroRuleId(v === '__none__' ? '' : v)
                        }
                        disabled={readOnly || !inboxId || macrosLoading}
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue
                            placeholder={
                              macrosLoading
                                ? 'Carregando…'
                                : availableMacros.length === 0
                                  ? 'Nenhuma macro disponível'
                                  : 'Sem macro'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sem macro —</SelectItem>
                          {availableMacros.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              <span className="font-medium">{m.name}</span>
                              {m.description && (
                                <span className="text-muted-foreground ml-1">
                                  — {m.description}
                                </span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {linkedMacroRuleId && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 shrink-0"
                          onClick={() => setLinkedMacroRuleId('')}
                          title="Remover macro vinculada"
                          disabled={readOnly}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Ordem</Label>
                    <Select
                      value={linkedMacroMode}
                      onValueChange={(v) =>
                        setLinkedMacroMode(
                          v as 'after_message' | 'before_message' | 'replace_message',
                        )
                      }
                      disabled={readOnly || !linkedMacroRuleId}
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="after_message">Depois da mensagem</SelectItem>
                        <SelectItem value="before_message">Antes da mensagem</SelectItem>
                        <SelectItem value="replace_message">
                          Substituir mensagem
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {!inboxId && (
                  <p className="text-[10px] text-muted-foreground pt-1">
                    Inbox da conversa não identificada — selecione uma conversa
                    para habilitar a vinculação de macros.
                  </p>
                )}
                {inboxId && !macrosLoading && availableMacros.length === 0 && (
                  <p className="text-[10px] text-muted-foreground pt-1">
                    Esta inbox ainda não possui ações rápidas cadastradas.
                  </p>
                )}
              </FormSectionCard>



              {/* --- SEÇÃO: Opções --- */}
              <FormSectionCard title="Opções" icon={CalendarClock}>
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-0.5 flex-1">
                    <Label htmlFor="sched-cancel" className="text-sm">
                      Cancelar se o contato responder
                    </Label>
                    <p className="text-[11px] text-muted-foreground">
                      Se o contato enviar qualquer mensagem antes do horário,
                      o envio é automaticamente cancelado.
                    </p>
                  </div>
                  <Switch
                    id="sched-cancel"
                    checked={cancelOnReply}
                    onCheckedChange={setCancelOnReply}
                    disabled={readOnly}
                  />
                </div>

                <div className="space-y-1.5 pt-2 border-t border-border/40">
                  <Label
                    htmlFor="sched-note"
                    className="text-xs flex items-center gap-1.5"
                  >
                    <StickyNote className="h-3 w-3 text-muted-foreground" />
                    Nota interna (opcional)
                  </Label>
                  <Input
                    id="sched-note"
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    placeholder="Ex.: Follow-up proposta enviada hoje"
                    maxLength={255}
                    className="h-9 text-xs"
                    disabled={readOnly}
                  />
                </div>
              </FormSectionCard>
            </div>
          </ScrollArea>

          {/* ===== PAINEL DIREITO — PREVIEW ===== */}
          <aside className="hidden lg:flex flex-col border-l bg-muted/20 max-h-[calc(90vh-140px)]">
            <div className="flex items-center gap-2 px-4 py-3 border-b bg-background/80 backdrop-blur-sm flex-shrink-0">
              <Eye className="w-3.5 h-3.5 text-muted-foreground" />
              <div>
                <h3 className="text-xs font-semibold">Preview</h3>
                <p className="text-[10px] text-muted-foreground">
                  Visualização em tempo real
                </p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {!channelType ? (
                <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
                    <MessageSquare className="w-5 h-5 text-muted-foreground/50" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Canal da conversa não identificado.
                  </p>
                </div>
              ) : (
                <ReminderMessagePreview
                  channelType={channelType}
                  inboxName={inboxName}
                  messageText={text}
                  messageType="text"
                />
              )}
            </div>
          </aside>
        </div>

        {/* ===== FOOTER ===== */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="h-7 px-2 sm:px-3"
          >
            {readOnly ? 'Fechar' : 'Cancelar'}
          </Button>
          {!readOnly && (
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="h-7 px-2 sm:px-3 min-w-[120px] gap-1.5"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {isEdit ? 'Salvar alterações' : 'Agendar envio'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Modal de Histórico — reaproveita listagem existente */}
    {conversationId && (
      <ConversationScheduledMessagesModal
        open={openHistory}
        onOpenChange={setOpenHistory}
        conversationId={conversationId}
        conversationContext={conversationContext}
      />
    )}
    </>
  );
}
